import { useCallback } from "react"

const SUNRISE = "sunrise"
const SUNSET = "sunset"
const DELIM_AT = "@"
const DELIM_DASH = "-"
const TIME_REGEX = /^[0-9]{2}:[0-9]{2}(:[0-9]{2})?$/

export type WittyPiScheduleLine = {
    start?: string
    end?: string
    preBuffer?: number
    postBuffer?: number
    type?: typeof SUNRISE | typeof SUNSET | "both" | "interval"
}

export type WittyPiSchedule = {
    lines: WittyPiScheduleLine[]
    dayPattern?: [boolean, boolean, boolean, boolean, boolean, boolean, boolean]
}

export type WittyPiInput = (
    | string
    | {
          0: string
          1: string
          2?: string
      }
)[]

export default () => {
    const parseTime = useCallback((input: string) => {
        const value = +input.slice(1)
        return input.startsWith("H") ? 60 * value : value
    }, [])

    const parseTimeInterval = useCallback((time: string, interval: string, add: boolean) => {
        const getAdjustedTimeComponent = (base: string, indicator: string) =>
            interval.includes(indicator)
                ? (+base + (add ? +interval.slice(1) : -interval.slice(1))).toString().padStart(2, "0")
                : base
        const [h, m, s] = time.split(":")

        return `${getAdjustedTimeComponent(h, "H")}:${getAdjustedTimeComponent(m, "M")}:${getAdjustedTimeComponent(
            s,
            "S",
        )}`
    }, [])

    const parseRelativeString = useCallback(
        (line: WittyPiScheduleLine, str: string) => {
            const [pre, post] = str.split(DELIM_AT)
            if (pre === SUNRISE || post === SUNRISE) {
                line.type = SUNRISE
                if (pre === SUNRISE) line.postBuffer = parseTime(post)
                else line.preBuffer = parseTime(pre)
            } else if (pre === SUNSET || post === SUNSET) {
                line.type = SUNSET
                if (pre === SUNSET) line.postBuffer = parseTime(post)
                else line.preBuffer = parseTime(pre)
            }
        },
        [parseTime],
    )

    const parse = useCallback(
        (wpistr: WittyPiInput) => {
            const lines: WittyPiScheduleLine[] = []

            wpistr.forEach(input => {
                const line: WittyPiScheduleLine = {}

                if (typeof input === "string") {
                    let [pre, post] = input.split(DELIM_AT)

                    if (pre && post) {
                        if (TIME_REGEX.test(pre)) {
                            line.type = "interval"
                            line.start = pre
                            line.end = parseTimeInterval(pre, post, true)
                        } else if (TIME_REGEX.test(post)) {
                            line.type = "interval"
                            line.start = parseTimeInterval(post, pre, false)
                            line.end = post
                        } else parseRelativeString(line, input)
                    } else {
                        ;[pre, post] = input.split(DELIM_DASH)
                        if (pre && post && TIME_REGEX.test(pre) && TIME_REGEX.test(post)) {
                            line.type = "interval"
                            line.start = pre
                            line.end = post
                        }
                    }
                } else {
                    parseRelativeString(line, input[0])
                    parseRelativeString(line, input[1])
                    if (input[2] === `${SUNRISE}-${SUNSET}`) line.type = "both"
                }

                lines.push(line)
            })

            return lines
        },
        [parseRelativeString, parseTimeInterval],
    )

    const stringify = useCallback((schedule: WittyPiScheduleLine[]) => {
        const out: WittyPiInput = []

        schedule.forEach(line => {
            if (line.type === "interval" && line.start && line.end)
                out.push(`${line.start}:00${DELIM_DASH}${line.end}:00`)
            else if (line.preBuffer && line.postBuffer) {
                if (line.type === "both")
                    out.push({
                        0: `M${line.preBuffer}${DELIM_AT}${SUNRISE}`,
                        1: `${SUNSET}${DELIM_AT}M${line.postBuffer}`,
                        2: `${SUNRISE}-${SUNSET}`,
                    })
                else
                    out.push({
                        0: `M${line.preBuffer}${DELIM_AT}${line.type}`,
                        1: `${line.type}${DELIM_AT}M${line.postBuffer}`,
                    })
            } else if (line.preBuffer) out.push(`M${line.preBuffer}${DELIM_AT}${line.type}`)
            else if (line.postBuffer) out.push(`${line.type}${DELIM_AT}M${line.postBuffer}`)
        })

        return out
    }, [])

    return { parse, stringify }
}
